// src/components/NavbarSearch.js
// #37 storefront search (2026-08-04): customer-facing search input for the
// storefront navbar. Debounced (300ms) dropdown with grouped top results
// (products / care guides / listed animals) + "See all results".
// Enter -> /search?q=..., Escape/blur closes the dropdown.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { searchStorefront } from '../services/api';
import { searchSitePages } from '../constants/siteSearchIndex';
import '../styles/navbarSearch.css';

const groupTitleStyle = {
  fontSize: '0.72rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--ff-text-secondary, #6c757d)',
  padding: '6px 12px 2px',
};

const rowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '6px 12px',
  cursor: 'pointer',
  color: 'var(--ff-text-primary, #212529)',
  textDecoration: 'none',
};

// `className` lets the navbar mount two independent instances (a desktop
// one inside the collapsed region, a mobile one that is always visible)
// and control which is displayed purely in CSS. Behavior is identical in
// both — no props change search wiring. See navbarSearch.css "#37b".
const NavbarSearch = ({ className = '' }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  // M1: monotonically increasing request id — a stale (slower) response
  // must never overwrite a newer one
  const requestIdRef = useRef(0);

  // Pages group: purely client-side, computed synchronously (no debounce
  // wait) so it renders instantly while the API groups load
  const pages = useMemo(() => searchSitePages(query), [query]);

  // Debounced fetch (300ms)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      // M1: dropping below the minimum clears everything, including any
      // in-flight response's right to render
      requestIdRef.current += 1;
      setResults(null);
      setOpen(false);
      setLoading(false);
      setSearchError(false);
      return;
    }
    setLoading(true);
    setSearchError(false);
    setOpen(true); // instant Pages group shows while API results load
    debounceRef.current = setTimeout(async () => {
      const rid = ++requestIdRef.current;
      try {
        const data = await searchStorefront(q);
        if (requestIdRef.current !== rid) return; // stale response
        setResults(data);
        setOpen(true);
      } catch (err) {
        if (requestIdRef.current !== rid) return;
        // M4: throttle/network failure is NOT "no results" — say so
        console.warn('[NavbarSearch] search failed:', err);
        setResults(null);
        setSearchError(true);
        setOpen(true);
      } finally {
        if (requestIdRef.current === rid) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // Close on click outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const goToResults = () => {
    const q = query.trim();
    if (q.length < 2) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      goToResults();
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const hasApiResults = results && (
    results.products?.length || results.care_guides?.length || results.animals?.length
  );
  const hasAny = hasApiResults || pages.length > 0;

  // #103 zero-inventory degradation: the server emits `group_order`
  // (care guides promoted to the top when the products group is empty).
  // Unknown names are dropped; older payloads without the key keep
  // today's order.
  const DEFAULT_GROUP_ORDER = ['products', 'animals', 'care_guides'];
  const serverGroupOrder = (Array.isArray(results?.group_order)
    ? results.group_order : []
  ).filter((g) => DEFAULT_GROUP_ORDER.includes(g));
  const apiGroupOrder = serverGroupOrder.length > 0
    ? serverGroupOrder : DEFAULT_GROUP_ORDER;

  const renderGroup = (title, items, renderItem) => {
    if (!items || items.length === 0) return null;
    return (
      <div>
        <div style={groupTitleStyle}>{title}</div>
        {items.slice(0, 4).map(renderItem)}
      </div>
    );
  };

  return (
    <div ref={containerRef} className={`ff-navbar-search${className ? ` ${className}` : ''}`}>
      <div style={{ position: 'relative' }}>
        <Search size={15} className="ff-navbar-search-icon" />
        <input
          type="search"
          aria-label="Search the store"
          placeholder="Search the store…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (results) setOpen(true); }}
          className="ff-navbar-search-input"
        />
      </div>

      {/* M3: plain div, no fake listbox ARIA (no option semantics/keyboard
          nav implemented — rows are plain click targets) */}
      {open && query.trim().length >= 2 && (
        <div
          aria-label="Search results"
          className="ff-navbar-search-dropdown"
          onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
        >
          {/* Pages group renders INSTANTLY (local index) and sits ABOVE
              products — navigation intent usually dominates. Real <Link>s
              (punch #50 pattern). */}
          {renderGroup('Pages', pages, (pg) => (
            <Link
              key={pg.path}
              to={pg.path}
              className="ff-navbar-search-row"
              style={rowStyle}
              onClick={() => { setOpen(false); setQuery(''); }}
            >
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {pg.label}
              </span>
              <span style={{ color: 'var(--ff-text-secondary, #6c757d)', fontSize: '0.8rem', flexShrink: 0 }}>
                Page
              </span>
            </Link>
          ))}
          {loading && (
            <div style={{ ...rowStyle, cursor: 'default', color: 'var(--ff-text-secondary, #6c757d)' }}>
              Searching…
            </div>
          )}
          {!loading && searchError && (
            <div style={{ ...rowStyle, cursor: 'default', color: 'var(--ff-text-secondary, #6c757d)' }}>
              Search is busy — try again.
            </div>
          )}
          {!loading && !searchError && !hasAny && (
            <div style={{ ...rowStyle, cursor: 'default', color: 'var(--ff-text-secondary, #6c757d)' }}>
              No results for "{query.trim()}"
            </div>
          )}
          {!loading && !searchError && hasApiResults && (
            <>
              {/* #110b: every row carries a thumbnail + the species
                  locality (quoted, storefront convention) — benchmark
                  competitors all show both. Locality arrives null when
                  the name already embeds it (server dedupes).
                  #103: groups render in the server's `group_order` (care
                  guides first when nothing is for sale). */}
              {apiGroupOrder.map((groupKey) => (
                <React.Fragment key={groupKey}>
                  {groupKey === 'products' && renderGroup('Products', results.products, (p) => (
                    <Link key={p.id} to={p.url} className="ff-navbar-search-row" style={rowStyle} onClick={() => { setOpen(false); setQuery(''); }}>
                      {p.thumb_url && (
                        <img src={p.thumb_url} alt="" width={32} height={32}
                             style={{ objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />
                      )}
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.name}
                        {p.locality && (
                          <span style={{ color: 'var(--ff-text-secondary, #6c757d)' }}>
                            {' '}"{p.locality}"
                          </span>
                        )}
                      </span>
                      {p.price_display && (
                        <span style={{ color: 'var(--ff-text-secondary, #6c757d)', fontSize: '0.8rem', flexShrink: 0 }}>
                          {p.price_display}
                        </span>
                      )}
                    </Link>
                  ))}
                  {groupKey === 'animals' && renderGroup('Available Animals', results.animals, (a) => (
                    <Link key={a.id} to={a.url} className="ff-navbar-search-row" style={rowStyle} onClick={() => { setOpen(false); setQuery(''); }}>
                      {a.thumb_url && (
                        <img src={a.thumb_url} alt="" width={32} height={32}
                             style={{ objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />
                      )}
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.name}
                        {a.locality && (
                          <span style={{ color: 'var(--ff-text-secondary, #6c757d)' }}>
                            {' '}"{a.locality}"
                          </span>
                        )}
                      </span>
                      <span style={{ color: 'var(--ff-text-secondary, #6c757d)', fontSize: '0.8rem', flexShrink: 0 }}>
                        {a.price_display}
                      </span>
                    </Link>
                  ))}
                  {groupKey === 'care_guides' && renderGroup('Care Guides', results.care_guides, (g) => (
                    <Link key={g.species_id} to={g.url} className="ff-navbar-search-row" style={rowStyle} onClick={() => { setOpen(false); setQuery(''); }}>
                      {g.thumb_url && (
                        <img src={g.thumb_url} alt="" width={32} height={32}
                             style={{ objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />
                      )}
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {g.title}{g.common_name ? ` — ${g.common_name}` : ''}
                      </span>
                    </Link>
                  ))}
                </React.Fragment>
              ))}
              <Link
                to={`/search?q=${encodeURIComponent(query.trim())}`}
                className="ff-navbar-search-row"
                style={{ ...rowStyle, justifyContent: 'center', fontWeight: 600, borderTop: '1px solid var(--ff-border-medium, #dee2e6)', marginTop: '4px', paddingTop: '8px', color: 'var(--ff-link)' }}
                onClick={() => setOpen(false)}
              >
                See all results
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default NavbarSearch;
